import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiArrowRight } from 'react-icons/fi';
import { getCatalog } from '../api/catalog';
import { categoryCover } from '../utils/categoryCovers';
import { setSeoMeta, toAbsoluteUrl, upsertJsonLd, removeJsonLd } from '../utils/seo';
import './Home.css';

const POPULAR_TONES = ['tone-terracotta', 'tone-slate', 'tone-sand', 'tone-lilac', 'tone-mint', 'tone-blue'];
const BENTO_ART = {
  ванны: '/images/bento/bathtub-lavender.png',
};

function catTitle(c) {
  return c?.name?.ru || c?.slug || '';
}

function catImage(slug) {
  return BENTO_ART[slug] || categoryCover(slug);
}

function subCount(c) {
  const n = Array.isArray(c?.subcategories) ? c.subcategories.length : 0;
  if (!n) return 'Смотреть раздел';
  return `${n} ${n === 1 ? 'подраздел' : n < 5 ? 'подраздела' : 'подразделов'}`;
}

export default function Home() {
  const [cats, setCats] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setCatsLoading(true);
    (async () => {
      try {
        const catalog = await getCatalog();
        if (!alive) return;
        const normCats = Array.isArray(catalog)
          ? catalog.filter((c) => c && c.slug && (c.name?.ru || c.slug))
          : [];
        setCats(normCats);
      } catch {
        /* ignore */
      } finally {
        if (alive) setCatsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const canonical = toAbsoluteUrl('/');
    setSeoMeta({
      title: 'LavSan — интернет-магазин сантехники',
      description:
        'LavSan Store: смесители, ванны, унитазы, душевые, мебель для ванной. Доставка по Краснодарскому краю.',
      canonical,
      og: { type: 'website', url: canonical },
    });

    upsertJsonLd('breadcrumb-home', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: canonical },
      ],
    });

    return () => removeJsonLd('breadcrumb-home');
  }, []);

  const featured = useMemo(
    () => cats.find((c) => c.slug === 'ванны') || cats[0] || null,
    [cats]
  );
  const wideCat = useMemo(
    () => cats.find((c) => c.slug === 'душевые-программы') || null,
    [cats]
  );
  const popularCats = useMemo(() => {
    const skip = new Set([featured?.slug, wideCat?.slug].filter(Boolean));
    const rest = cats.filter((c) => !skip.has(c.slug));
    const fences = rest.filter((c) => c.slug === 'душевые-кабины-и-ограждения');
    const other = rest.filter((c) => c.slug !== 'душевые-кабины-и-ограждения');
    return [...fences, ...other];
  }, [cats, featured, wideCat]);

  return (
    <div className="home">
      <section className="home-hero" aria-label="LavSan">
        <picture>
          <source srcSet="/images/hero-bath.webp" type="image/webp" />
          <img
            className="home-hero__media"
            src="/images/hero-bath.jpg"
            alt=""
            width={2560}
            height={1445}
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <div className="home-hero__inner container">
          <div className="home-hero__copy">
            <p className="home-hero__brand">LavSan Store</p>
            <h1 className="home-hero__title">Сантехника для дома и объекта</h1>
            <p className="home-hero__lead">
              Розница и опт. Подбор и доставка по Краснодарскому краю.
            </p>
            <div className="home-hero__cta">
              <Link to="/catalog" className="home-hero__btn">
                В каталог <FiArrowRight />
              </Link>
              <Link to="/delivery" className="home-hero__btn home-hero__btn--ghost">
                Доставка
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="home__shell container">
        <section className="home-bento" aria-labelledby="home-cats-title">
          <div className="home-section-head">
            <div>
              <h2 id="home-cats-title">Категории</h2>
            </div>
            <Link to="/catalog" className="home-section-link">
              Весь каталог <FiArrowRight />
            </Link>
          </div>
          <div className="home-bento__grid">
            {catsLoading && !featured ? (
              <div className="bento-deals is-skeleton" />
            ) : featured ? (
              <Link
                to={`/catalog/${featured.slug}`}
                className="bento-deals"
                aria-label={catTitle(featured)}
              >
                <img
                  className="bento-cover"
                  src={catImage(featured.slug)}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = categoryCover(featured.slug);
                  }}
                />
                <div className="bento-veil" />
                <div className="bento-deals__content">
                  <span className="bento-deals__title">{catTitle(featured)}</span>
                  <p className="bento-deals__meta">{subCount(featured)}</p>
                  <span className="bento-fab is-light bento-deals__go" aria-hidden>
                    <FiArrowUpRight />
                  </span>
                </div>
              </Link>
            ) : (
              <div className="bento-deals">
                <div className="bento-veil" />
                <div className="bento-deals__content">
                  <p className="bento-empty">Категории скоро появятся</p>
                </div>
              </div>
            )}

            <Link
              to={wideCat ? `/catalog/${wideCat.slug}` : '/catalog'}
              className="bento-promo bento-promo--wide"
            >
              <img
                className="bento-cover"
                src={catImage(wideCat?.slug || 'душевые-программы')}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.opacity = '0';
                }}
              />
              <div className="bento-veil" />
              <div className="bento-promo__copy">
                <h2>{wideCat ? catTitle(wideCat) : 'Душевые программы'}</h2>
                <span className="bento-promo__badge">Открыть</span>
              </div>
            </Link>

            <div className="bento-popular" aria-label="Все категории">
              <div className="bento-popular__head">
                <h3>Все разделы</h3>
                <Link to="/catalog">
                  Весь каталог <FiArrowRight />
                </Link>
              </div>
              <div className="bento-popular__row">
                {(catsLoading
                  ? Array.from({ length: Math.max(popularCats.length, 7) })
                  : popularCats
                ).map((c, i) => {
                  if (!c) {
                    return (
                      <div
                        key={`sk-${i}`}
                        className={`bento-pop is-skeleton ${POPULAR_TONES[i % POPULAR_TONES.length]}`}
                      />
                    );
                  }
                  const title = catTitle(c);
                  return (
                    <Link
                      key={c.slug}
                      to={`/catalog/${c.slug}`}
                      className={`bento-pop ${POPULAR_TONES[i % POPULAR_TONES.length]}`}
                    >
                      <img
                        className="bento-cover"
                        src={catImage(c.slug)}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.opacity = '0';
                        }}
                      />
                      <div className="bento-veil bento-veil--pop" />
                      <div className="bento-pop__top">
                        <span className="bento-fab is-light" aria-hidden>
                          <FiArrowUpRight />
                        </span>
                      </div>
                      <div className="bento-pop__foot">
                        <div>
                          <span className="bento-pop__title">{title}</span>
                          <strong>{subCount(c)}</strong>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="home-banner">
          <div className="home-banner__media">
            <img src={catImage(cats[1]?.slug || 'ванны')} alt="" />
          </div>
          <div className="home-banner__copy">
            <p className="home-banner__eyebrow">Доставка по Краснодарскому краю</p>
            <h2>Сантехника для дома и объекта — без лишнего шума</h2>
            <p>
              Подберём комплект под ванную или кухню. Розница и оптовые условия для своих клиентов.
            </p>
            <Link to="/delivery" className="home-banner__cta">
              Условия доставки <FiArrowRight />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
